import { Modal, User } from '.';
import { ActivitiesMethods } from 'common/methods';
import { action, computed, observable, reaction } from 'mobx';
import * as moment from 'moment';
import { app } from './../App';

// Interfaces
import {
  IActivitiesInfo,
  IActivitiesGroup,
  IActivity,
  IActivities,
  IActivityTypeApi,
} from 'common/interfaces/activity';

// Store
export class Activities {
  constructor(private user: User, private modal: Modal) {
    setInterval(() => {
      this.setActivityReminders();
    }, 1000 * 60 * 60);
  }

  private reactToUserChange = reaction(
    () => this.user.id,
    (userId: number) => {
      if (userId !== null) {
        this.fetchActivityTypes();
        this.fetchUserActivities(userId);
      }
    }
  );

  // Reminders
  @observable public showRemindersCenter: boolean = false;

  // Activity types
  @observable public loadingAllActivityTypes: boolean = false;
  @observable public allActivityTypes: Array<{
    value: number;
    label: string;
  }> = [];

  // All activities
  @observable public allActivities: IActivity[] = [];
  @computed public get allActivitiesIds(): number[] {
    return this.allActivities.map((activity, i) => activity.id);
  }
  @observable public allActivitiesLoading: boolean = false;
  @observable public userActivities: IActivities[] = [];
  @observable public userActivitiesLoading: boolean = false;

  // Initialize all activities
  @action public initializeAllActivities = async (id: object): Promise<any> => {
    try {
      return await this.fetchAllActivities(id);
    } catch (err) {
      console.error(err);
      return err;
    }
  };

  // Reminder timeouts
  private reminderTimeouts: any[] = [];

  // List activities that need to be reminded about
  @computed public get activitiesToRemindAbout(): IActivity[] {
    const activities: IActivity[] = [];

    this.userActivities.map((userActivites) => {
      userActivites.activities.map((activity, i) => {
        if (
          !activity.finished &&
          activity.remind &&
          activity.showReminder &&
          activity.assignedToUserId === this.user.id
        ) {
          activities.push(activity);
        }
      });
    });

    return activities.sort((a, b) => {
      const remindDateTimeOfA = a.remindAt || a.activityTime;
      const remindDateTimeOfB = b.remindAt || b.activityTime;
      return remindDateTimeOfA > remindDateTimeOfB
        ? 1
        : remindDateTimeOfA < remindDateTimeOfB
        ? -1
        : 0;
    });
  }

  @computed public get activitiesToRemindAboutIds(): number[] {
    return this.activitiesToRemindAbout.map((activity) => activity.id);
  }

  @computed public get reminderLength(): object {
    return this.activitiesToRemindAbout;
  }

  @computed public get unfinishedActivitiesLength(): number {
    return this.allActivities.filter((a) => a.finished).length;
  }

  @computed public get activitiesLength(): number {
    return this.allActivities.length;
  }

  @computed public get activitiesUserLength(): number {
    if (this.userActivitiesIndex !== -1) {
      return this.userActivities[this.userActivitiesIndex].activities.length;
    } else {
      return 0;
    }
  }

  @computed public get activitiesGroups(): IActivitiesGroup[] {
    return ActivitiesMethods.parseActivitiesGroups(this.allActivities);
  }

  @computed public get userActivitiesIndex(): number {
    let userIndex = -1;

    this.userActivities.some((user, i) => {
      if (user.id === this.user.id) {
        userIndex = i;
        return true;
      }
    });

    return userIndex;
  }

  @computed public get userActivitiesIds(): number[] {
    if (this.userActivitiesIndex !== -1) {
      return this.userActivities[this.userActivitiesIndex].activities.map(
        (activity, i) => activity.id
      );
    } else {
      return [];
    }
  }

  @computed public get userActivitiesGroups(): IActivitiesGroup[] {
    return this.userActivitiesIndex !== -1
      ? ActivitiesMethods.parseActivitiesGroups(
          this.userActivities[this.userActivitiesIndex].activities
        )
      : [];
  }

  @action
  public openActivitiesReminders = () => {
    this.showRemindersCenter = true;
    document.documentElement.style.overflow = 'hidden';
  };

  @action
  public closeActivitiesReminders = () => {
    this.showRemindersCenter = false;
    document.documentElement.style.overflow = null;
  };

  // Fetch all activities
  @action public fetchAllActivities = async (id): Promise<IActivity[]> => {
    // Indicate that activities are loading
    this.allActivitiesLoading = true;

    try {
      // Get all activities visible to the user
      const activitiesService: any = app.service('activity');

      const activitiesInfo = (await activitiesService.find({
        query: {
          $limit: 10000,
          ...id,
        },
      })) as IActivitiesInfo;

      // Update state
      this.allActivities = activitiesInfo.data.map((activity, i) => ({
        ...activity,
        savingComplete: false,
      }));

      return activitiesInfo.data;
    } catch (err) {
      console.error(err);
      return err;
    } finally {
      this.allActivitiesLoading = false;
    }
  };

  // Fetch user activities
  @action public fetchUserActivities = async (id): Promise<IActivity[]> => {
    // Indicate that activities are loading
    this.userActivitiesLoading = true;

    try {
      // Get all activities visible to the user
      const activitiesService: any = app.service('activity');
      const activitiesInfo = (await activitiesService.find({
        query: {
          $limit: 10000,
          $skip: 0,
          finished: 0,
          assignedToUserId: id,
          $sort: {
            activityTime: 1,
          },
        },
      })) as IActivitiesInfo;

      // Check if open lead activities are already fetched
      let userIndex = null;
      this.userActivities.some((user, i) => {
        if (user.id === this.user.id) {
          userIndex = i;
          return true;
        }
      });

      // Update state
      if (userIndex !== null) {
        this.userActivities = this.userActivities
          .slice(0, userIndex)
          .concat({
            id: this.user.id,
            activities: activitiesInfo.data.map((activity, i) => ({
              ...activity,
              saving: false,
              showReminder: false,
            })),
          })
          .concat(this.userActivities.slice(userIndex + 1));
      } else {
        this.userActivities.push({
          id: this.user.id,
          activities: activitiesInfo.data.map((activity, i) => ({
            ...activity,
            saving: false,
            showReminder: false,
          })),
        });
      }

      this.setActivityReminders();

      // Return activities data
      return activitiesInfo.data;
    } catch (err) {
      console.error(err);
      return err;
    } finally {
      this.userActivitiesLoading = false;
    }
  };

  // Get all activity types
  @action public fetchActivityTypes = async (): Promise<any> => {
    try {
      this.loadingAllActivityTypes = true;
      const service = await app.service('activity-type');
      const activities: IActivityTypeApi = await service.find({
        query: { $limit: 100000 },
      });

      this.allActivityTypes = activities.data.map((activity) => ({
        value: activity.id,
        label: activity.name,
      }));

      this.loadingAllActivityTypes = false;

      return activities;
    } catch (err) {
      console.error(err);
      return err;
    }
  };

  // Disable reminder
  @action public disableActivityReminder = async (
    activityId: number
  ): Promise<boolean> => {
    try {
      const service = await app.service('activity');
      const update = service.patch(activityId, {
        remind: false,
      });

      this.userActivities.map((user) => {
        user.activities.map((activity) => {
          if (activity.id === activityId) {
            activity.remind = false;
          }
        });
      });

      return true;
    } catch (err) {
      console.error(err);
      return err;
    }
  };

  // Mark activity as complete
  @action public markActivityAsComplete = async (
    activityId: number
  ): Promise<boolean> => {
    try {
      const service = await app.service('activity');
      const update = service.patch(activityId, {
        finished: true,
      });

      this.userActivities.map((user) => {
        user.activities.map((activity) => {
          if (activity.id === activityId) {
            activity.finished = true;
          }
        });
      });

      return true;
    } catch (err) {
      console.error(err);
      return err;
    }
  };

  // Delay activity reminder by minutes
  @action public delayActivityReminder = async (
    activityId: number,
    delayReminderByMinutes: number
  ): Promise<boolean> => {
    try {
      let activity: IActivity = null;
      this.userActivities.some((user) => {
        return user.activities.some((userActivity) => {
          if (userActivity.id === activityId) {
            activity = userActivity;
            return true;
          }
        });
      });

      if (activity === null) {
        throw new Error(
          'Could not find matching activity to update remind time'
        );
      }

      const updatedRemindAt = moment().add(delayReminderByMinutes, 'minutes');
      const updatedRemindAtString: string = updatedRemindAt.format();

      const service = await app.service('activity');
      const update = service.patch(activityId, {
        remind: true,
        remindAt: updatedRemindAtString,
      });

      activity.remind = true;
      activity.showReminder = false;
      activity.remindAt = updatedRemindAtString;

      if (delayReminderByMinutes < 60 * 24) {
        this.reminderTimeouts.push(
          setTimeout(() => {
            this.showActivityReminder(activityId);
          }, delayReminderByMinutes * 1000 * 60)
        );
      }

      return true;
    } catch (err) {
      console.error(err);
      return err;
    }
  };

  // Show reminder to user
  @action private showActivityReminder = (activityId: number) => {
    this.userActivities.some((userActivities) => {
      return userActivities.activities.some((activity) => {
        if (activity.id === activityId) {
          if (activity.finished === false) {
            activity.remind = true;
            activity.showReminder = true;
          }
          return true;
        }
      });
    });
  };

  // Set reminders for all user activities
  @action private setActivityReminders = () => {
    let userActivitesIndex: number = -1;
    this.userActivities.some((user, i) => {
      if (user.id === this.user.id) {
        userActivitesIndex = i;
        return true;
      }
    });

    if (userActivitesIndex !== -1) {
      const now = moment();

      this.reminderTimeouts.map((reminder) => {
        clearTimeout(reminder);
      });

      this.reminderTimeouts = [];

      this.userActivities[userActivitesIndex].activities.map((activity, i) => {
        if (activity.remind && !activity.finished) {
          const remindAtDateAndTime =
            activity.remindAt || activity.activityTime;
          const difference = moment.duration(
            moment(remindAtDateAndTime).diff(now)
          );
          const differenceInMinutes = difference.asMinutes();
          const differenceInMiliseconds = difference.asMilliseconds();
          const timeoutTimer: number = differenceInMiliseconds || 0;

          if (differenceInMinutes < 60 * 24) {
            this.reminderTimeouts.push(
              setTimeout(() => {
                this.showActivityReminder(activity.id);
              }, timeoutTimer)
            );
          }
        }
      });
    }
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/stores/Activities.ts